import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  subscribeMemberInvitesForUser,
  type WebPendingInvite,
} from '../services/inviteService'
import { normalizeMemberEmail } from '../services/memberService'

export interface UseMemberInvitesResult {
  invites: WebPendingInvite[]
  count: number
  loading: boolean
  error: Error | null
  retry: () => void
}

interface AccountInvitesState {
  email: string
  invites: WebPendingInvite[]
  loading: boolean
  error: Error | null
}

/**
 * Shared lifecycle-safe Firestore subscription hook for received MEMBER invitations.
 * Manages listener registration, automatic cleanup on unmount, account/email changes,
 * and retry upon query errors.
 */
export const useMemberInvites = (email?: string | null): UseMemberInvitesResult => {
  const normalizedEmail = useMemo(() => normalizeMemberEmail(email), [email])
  const [retryTrigger, setRetryTrigger] = useState(0)

  const [state, setState] = useState<AccountInvitesState>(() => ({
    email: normalizedEmail,
    invites: [],
    loading: Boolean(normalizedEmail),
    error: null,
  }))

  const retry = useCallback(() => {
    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }))
    setRetryTrigger((prev) => prev + 1)
  }, [])

  useEffect(() => {
    if (!normalizedEmail) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState({
        email: '',
        invites: [],
        loading: false,
        error: null,
      })
      return
    }

    let isCurrent = true
    setState({
      email: normalizedEmail,

      invites: [],
      loading: true,
      error: null,
    })

    const unsubscribe = subscribeMemberInvitesForUser(
      normalizedEmail,
      (nextInvites) => {
        if (!isCurrent) return
        setState({
          email: normalizedEmail,
          invites: nextInvites,
          loading: false,
          error: null,
        })
      },
      (err) => {
        if (!isCurrent) return
        setState({
          email: normalizedEmail,
          invites: [],
          loading: false,
          error: err,
        })
      },
    )

    return () => {
      isCurrent = false
      unsubscribe()
    }
  }, [normalizedEmail, retryTrigger])

  const isMatchingEmail = state.email === normalizedEmail
  const activeInvites = isMatchingEmail ? state.invites : []
  const activeLoading = isMatchingEmail ? state.loading : Boolean(normalizedEmail)
  const activeError = isMatchingEmail ? state.error : null

  return {
    invites: activeInvites,
    count: activeInvites.length,
    loading: activeLoading,
    error: activeError,
    retry,
  }
}
